import { Link } from "react-router";
import { ComparePage } from "./ComparePage";

// Angle: SWITCHING. Readers on this page already run an MRR platform and are
// weighing a move, so the content is about the monthly loop end to end and how
// a switch is staged. Nothing here describes RxPertise itself.
export function RxpertiseAlternative() {
  return (
    <ComparePage
      slug="rxpertise-alternative"
      competitor="RxPertise"
      title="RxPertise Alternative for MRR | WeConsult Rx"
      description="Looking for an RxPertise alternative? WeConsult Rx runs the whole monthly consultant-pharmacy loop: chart ingest, rules + AI review, prescriber/DON e-sign, and invoicing."
      h1="The RxPertise alternative built for the whole monthly review."
      eyebrow="RxPertise alternative"
      answer="Looking for an alternative to RxPertise? WeConsult Rx is consultant pharmacist software that runs the entire monthly medication regimen review in one platform: chart ingest, rules-and-AI clinical review, prescriber and DON e-signature, and facility invoicing. It covers skilled nursing, ambulatory surgery, and dialysis, and every recommendation is kept as a dated, signed record for survey."
      criteriaHeading="Six questions to ask before you switch MRR platforms"
      criteriaLede="Put each one to every vendor on your shortlist. Here is how WeConsult Rx answers."
      criteria={[
        { label: "Does it cover the whole month, not just the review?", detail: "WeConsult Rx runs chart ingest, clinical review, prescriber and DON sign-off, and the invoice as one workflow, so nothing is re-keyed between tools." },
        { label: "Is e-signature built in?", detail: "Prescribers and Directors of Nursing sign the recommendation bundle inside the platform or through the facility portal, with no separate signing service to export to." },
        { label: "Does billing come out of the same record?", detail: "Facility invoices are generated from the reviews you closed, on a schedule you set, and can sync to QuickBooks Online." },
        { label: "How do charts get in?", detail: "Upload the month's EHR exports (PDF or CSV, including PointClickCare reports) and the regimen is parsed per resident, ready for review." },
        { label: "Will the documentation hold up at survey?", detail: "Each flag, recommendation, prescriber response, and signature is time-stamped in an append-only audit trail aligned to CMS F-tag guidance (F756, F757, F605)." },
        { label: "Can one platform serve every facility type?", detail: "Separate rule packs for SNF, ambulatory surgery centers, and dialysis facilities, so a mixed book of business stays in one system." },
      ]}
      sections={[
        {
          heading: "How a switch to WeConsult Rx is staged",
          body: (
            <>
              <p>
                Most teams change platforms between monthly cycles, one facility group at a time, so no review
                is ever left half in one system and half in another.
              </p>
              <ul>
                <li><strong>Pilot facility.</strong> Run one facility's month in WeConsult Rx alongside your current process and compare time per review and the finished documentation.</li>
                <li><strong>Load the month's charts.</strong> Upload the EHR exports you already pull; residents and regimens are parsed for review.</li>
                <li><strong>Set up signers and billing.</strong> Add each facility's prescribers and DON, then your invoice schedule, so the first live month closes end to end.</li>
                <li><strong>Roll out by facility group.</strong> Move remaining facilities at the next cycle boundary.</li>
              </ul>
            </>
          ),
        },
        {
          heading: "What changes for the consultant pharmacist",
          body: (
            <>
              <p>
                Rules for Beers, STOPP, anticholinergic burden, and gradual dose reduction surface the flags; AI
                drafts the rationale; you edit and finalize every recommendation. The software removes the typing and
                chasing, not the clinical judgment.
              </p>
              <p>
                Unsigned recommendations stay visible until a prescriber responds, so follow-up is a queue rather
                than a spreadsheet. See the full loop on the <Link to="/platform">platform overview</Link>, or
                estimate the time you would get back with the <Link to="/roi-calculator">ROI calculator</Link>.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        {
          q: "What is a good alternative to RxPertise for consultant pharmacists?",
          a: "WeConsult Rx is an alternative built around the full monthly loop: chart ingest, rules-and-AI review, prescriber and DON e-signature, and invoicing in one platform for SNF, ASC, and dialysis. The fairest way to decide is a side-by-side demo on a sample facility.",
        },
        {
          q: "How hard is it to switch from RxPertise to WeConsult Rx?",
          a: "Most teams start with one pilot facility for a single monthly cycle, then move remaining facilities at the next cycle boundary. Charts come in from the EHR exports you already pull, and our team helps set up facilities, signers, and invoice schedules.",
        },
        {
          q: "Can prescribers sign recommendations without another tool?",
          a: "Yes. Prescribers and DONs e-sign the recommendation bundle inside WeConsult Rx or through the facility portal, and each signature is time-stamped in the audit trail.",
        },
        {
          q: "Does WeConsult Rx handle invoicing for consultant pharmacy services?",
          a: "Yes. Invoices are generated from the reviews you closed, on the schedule you set per facility, and can sync to QuickBooks Online.",
        },
        {
          q: "Do demos use real patient data?",
          a: "No. Every demo uses synthetic data only, never real resident PHI. WeConsult Rx is built for a HIPAA-conscious workflow with a BAA available.",
        },
      ]}
      related={[
        { to: "/medication-regimen-review-software", label: "MRR software", desc: "How the monthly review runs, step by step." },
        { to: "/survey-readiness", label: "Survey readiness", desc: "F-tag documentation that holds up at survey." },
        { to: "/roi-calculator", label: "ROI calculator", desc: "Estimate hours saved per month." },
      ]}
      cta={{
        title: "Compare it on one of your facilities",
        body: "Book a side-by-side demo on a sample facility, or start with a free first-facility review.",
      }}
    />
  );
}
